import {
  Button,
  Collapse,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hook';
import {
  minNumberOfTrips,
  showTripImprovementBasedOnShutdown,
} from 'utils/enums';
import { convertSecondsWithUnitValues } from 'utils/helpers/dateTime';
import { getLabelWrtUnitSystem } from 'utils/helpers/unitSystem';
import { formatTripData } from 'views/Production/Hauler/component/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  data: any;
  setSuggestionData: any;
}

const SuggestionItems = ({
  enableDarkTheme,
  data,
  setSuggestionData,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const accentColor = palette.buttonLink ?? palette.primary;
  const dividerColor = palette.cardBorderColor ?? palette.border;
  const { t } = useTranslation();
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const customerCode = useAppSelector((state) => state.persistedReducer)
    .customer.code;
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const {
    totalTripTime,
    averageDistance,
    totalDistance,
    outlierTripsBasedOnTime,
  } = formatTripData(data, unitSystem, preferredTimeDisplayMode, timezone);

  const [showOutliers, setShowOutliers] = useState(false);

  const convertTimeToSeconds = (timeStr: string) => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };
  const totalTrips = data?.total_trips;
  const totalFuelConsumedInAllTrips = data?.total_fuel_consumed;
  const totalExcessIdling = convertSecondsWithUnitValues(
    data?.total_excess_idling
  );
  const shutdownTime = convertSecondsWithUnitValues(data?.haulers_shutdown);
  const groupedOutliers =
    outlierTripsBasedOnTime !== 'None'
      ? (outlierTripsBasedOnTime as string[]).reduce(
          (acc: any, item: string) => {
            const [trip, asset] = item.split(' for ');
            const [assetId, tripDetails] = asset.split(' with trip ');
            const [, tripTime] = tripDetails.split('of ');

            if (!acc[assetId]) {
              acc[assetId] = [];
            }

            acc[assetId].push({ trip, assetId, tripDetails, tripTime });
            return acc;
          },
          {}
        )
      : {};

  Object.keys(groupedOutliers).forEach((assetId) => {
    groupedOutliers[assetId].sort((a: any, b: any) => {
      return (
        convertTimeToSeconds(b.tripTime) - convertTimeToSeconds(a.tripTime)
      );
    });
  });
  const navigate = useNavigate();
  const { assets } = useAppSelector((state) => state.assetReducer);

  const handleTripClick = (assetId: string, trip: any) => {
    function getAssetVinByBumperNumber(bumperNumber: string): string | null {
      const asset = assets.find((a) => a.bumperNumber === bumperNumber);
      return asset ? asset.assetVin : null;
    }
    const assetVin = getAssetVinByBumperNumber(assetId);
    const date = String(trip?.trip).split('on')?.[1];

    // adding selected trip in location as well as when navigating same trip is selected as well
    const tripMatch = trip?.trip?.match(/\d+/);
    const tripNumber = tripMatch ? Number(tripMatch[0]) : null;

    navigate(`/production/load-cycle-analysis/${assetVin}`, {
      state: {
        type: 'trip-details',
        value: 0,
        asset: assetId,
        date,
        selectedTrip: tripNumber,
      },
    });
  };

  useEffect(() => {
    setSuggestionData({
      facts: {
        'Avg trip time for selected period was': convertSecondsWithUnitValues(
          data?.average_valid_trip_time
        ),
        'Total No of Trips': totalTrips,
        'Total Trip Time': totalTripTime,
        'Total Distance': `${totalDistance} ${getLabelWrtUnitSystem(
          'KM',
          unitSystem
        ).toLowerCase()}`,
        'Avg. distance per trip': `${averageDistance} ${getLabelWrtUnitSystem(
          'KM',
          unitSystem
        ).toLowerCase()}`,
      },
      recommendations: {
        'Potential Fuel savings': `${(
          (data?.potential_fuel_saving / data?.total_fuel_consumed) *
          100
        ).toFixed(2)}% of fuel out of ${Number(
          totalFuelConsumedInAllTrips
        ).toFixed(2)} ${getLabelWrtUnitSystem(
          'L',
          unitSystem
        ).toLowerCase()} of total fuel consumed over all trips)`,
        'Potential Time Savings': `${totalExcessIdling} (${(
          (data?.total_excess_idling / data?.total_time_in_trips.toFixed(2)) *
          100
        ).toFixed(2)}%)`,
        'Potential Trip Improvements': `${Math.floor(
          data?.potential_trip_improvement
        )}`,
        'Potential Tonnage Increase': `${data?.potential_tonnage_increase.toFixed(
          2
        )}`,
        'Number of Outlier Trips': `${data?.outlier_trips_based_on_time?.length}`,
        ...(showTripImprovementBasedOnShutdown[customerCode] && {
          'Potential Trips Improvement Based on Shutdown': `${
            data?.total_trips >= minNumberOfTrips[customerCode]
              ? Math.floor(data?.potential_shutdown_improvement)
              : 0
          }`,
        }),
      },
    });
  }, [data]);

  return (
    <>
      <ListItem
        sx={{
          borderBottom: `1px solid ${dividerColor}`,
          paddingBottom: '16px',
          display: 'flex',
          direction: 'row',
        }}
      >
        <ListItemText
          primary={
            <Typography
              sx={{
                color: palette.textPrimary,
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              <span
                style={{
                  color: palette.textPrimary,
                }}
              >
                {t('facts')}
              </span>
            </Typography>
          }
          secondary={
            <>
              <Typography
                sx={{
                  color: palette.textPrimary,
                  fontSize: '12px',
                  paddingTop: '5px',
                }}
              >
                <p>
                  {t('avg_trip_time_for_selected_period_was')}{' '}
                  <b>
                    {convertSecondsWithUnitValues(
                      data?.average_valid_trip_time
                    )}
                  </b>{' '}
                </p>
                <p>
                  {t('total_no_of_trips')} <b>{totalTrips}</b>
                </p>
                <p>
                  {t('total_tonnage')} <b>{data?.total_max_load_ton} Tons</b>
                </p>
                <p>
                  {t('total_trip_time_2')} <b>{totalTripTime}</b>
                </p>
                <p>
                  {t('total_fuel_consumed_2')}{' '}
                  <b>
                    {totalFuelConsumedInAllTrips}{' '}
                    {getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}
                  </b>
                </p>
                <p>
                  {t('total_distance')}{' '}
                  <b>
                    {totalDistance}{' '}
                    {getLabelWrtUnitSystem('KM', unitSystem).toLowerCase()}.
                  </b>
                </p>
                <p>
                  {t('avg_distance_per_trip')}{' '}
                  <b>
                    {averageDistance}{' '}
                    {getLabelWrtUnitSystem('KM', unitSystem).toLowerCase()}.
                  </b>
                </p>
              </Typography>
            </>
          }
        />
      </ListItem>

      <ListItem
        sx={{
          borderBottom: `1px solid ${dividerColor}`,
          paddingBottom: '16px',
          display: 'flex',
          direction: 'row',
        }}
      >
        <ListItemText
          primary={
            <Typography
              sx={{
                color: palette.textPrimary,
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              <span
                style={{
                  color: palette.textPrimary,
                }}
              >
                Recommendations
              </span>
            </Typography>
          }
          secondary={
            <>
              <Typography
                sx={{
                  color: palette.textPrimary,
                  fontSize: '12px',
                  paddingTop: '5px',
                }}
              >
                <p>
                  {t('potential_fuel_savings')}{' '}
                  <b>
                    {data?.potential_fuel_saving.toFixed(2)}{' '}
                    {getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}
                  </b>
                  {' ('}
                  <b>
                    {(
                      (data?.potential_fuel_saving /
                        data?.total_fuel_consumed.toFixed(2)) *
                      100
                    ).toFixed(2)}
                    %
                  </b>
                  {' of'}{' '}
                  <b>
                    {totalFuelConsumedInAllTrips.toFixed(2)}{' '}
                    {getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}{' '}
                  </b>{' '}
                  {t('fuel_can_be_saved')}
                </p>
                <p>
                  {t('potential_time_savings_2')} <b>{totalExcessIdling}</b>
                  {' ('}
                  <b>
                    {(
                      (data?.total_excess_idling /
                        data?.total_time_in_trips.toFixed(2)) *
                      100
                    ).toFixed(2)}
                    %
                  </b>
                  {' of'} <b>{totalTripTime}</b>{' '}
                  {t('total_trip_time_was_excess_idling')} {')'}
                </p>
                <p>
                  {t(
                    'potential_tonnage_increase_based_on_asset_specifications'
                  )}{' '}
                  <b>{data?.potential_tonnage_increase.toFixed(2)} Tons </b>{' '}
                  {data?.potential_tonnage_increase > 0 && (
                    <span>
                      {'('}
                      {t(
                        'this_means_asset_could_have_hauled_more_tonnage_in_the_given'
                      )}{' '}
                      <b>{data?.total_max_load_ton.toFixed(2)} Tons</b>{' '}
                      {t(
                        'and_maximum_possible_tonnage_that_could_have_been_hauled_was'
                      )}{' '}
                      <b>{data?.max_possible_tonnage.toFixed(2)} Tons</b>.{')'}
                    </span>
                  )}
                </p>
                <p>
                  {t(
                    'potential_trips_that_can_be_increased_based_on_excess_idling'
                  )}{' '}
                  {Math.floor(data?.potential_trip_improvement) > 1
                    ? 'are'
                    : 'is'}{' '}
                  <b>{Math.floor(data?.potential_trip_improvement ?? 0)}</b>
                </p>
                {showTripImprovementBasedOnShutdown[customerCode] &&
                  (Math.floor(data?.potential_shutdown_improvement) > 1 &&
                  data?.total_trips >= minNumberOfTrips[customerCode] ? (
                    <p>
                      {t(
                        'potential_trips_that_can_be_increased_based_on_shutdown'
                      )}{' '}
                      {Math.floor(data?.potential_shutdown_improvement) > 1 &&
                      data?.total_trips >= minNumberOfTrips[customerCode]
                        ? 'are'
                        : 'is'}{' '}
                      <b>
                        {Math.round(
                          data?.total_trips >= minNumberOfTrips[customerCode]
                            ? data?.potential_shutdown_improvement ?? 0
                            : 0
                        )}
                      </b>{' '}
                      {t('there_is_total')} <b>{shutdownTime}</b>{' '}
                      {t('of_shutdown_time')}
                    </p>
                  ) : (
                    <p>
                      {t(
                        'potential_trips_that_can_be_increased_based_on_shutdown_is_0'
                      )}
                    </p>
                  ))}
                <p>
                  {t('number_of_outlier_trips')}{' '}
                  <b>{data?.outlier_trips_based_on_time.length}</b>
                </p>
              </Typography>
            </>
          }
        />
      </ListItem>

      <ListItem data-no-pdf-export="true">
        <Button
          onClick={() => {
            setShowOutliers(!showOutliers);
          }}
          sx={{
            color: accentColor,
            fontSize: '12px',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: palette.sectionBackground,
              color: palette.primaryDark ?? accentColor,
            },
          }}
        >
          {showOutliers
            ? 'Outlier Trips: collapse'
            : 'To see Outlier Trips: Click to expand'}
        </Button>
      </ListItem>

      <Collapse in={showOutliers} data-no-pdf-export="true">
        {Object.keys(groupedOutliers).map((assetId: string) => (
          <ListItem
            key={assetId}
            sx={{
              borderBottom: `1px solid ${dividerColor}`,
              paddingBottom: '16px',
            }}
          >
            <ListItemText
              primary={
                <Typography
                  sx={{
                    color: accentColor,
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  {assetId}
                </Typography>
              }
              secondary={
                <Typography
                  sx={{
                    color: palette.textPrimary,
                    fontSize: '10px',
                    paddingTop: '5px',
                  }}
                >
                  {groupedOutliers[assetId].map((trip: any, index: number) => (
                    <p key={index}>
                      <span
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: accentColor,
                        }}
                        onClick={() => {
                          handleTripClick(assetId, trip);
                        }}
                      >
                        {trip.trip}
                      </span>

                      <span>
                        {' '}
                        took {trip.tripDetails.split('time of')?.[1]}
                      </span>
                    </p>
                  ))}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </Collapse>
    </>
  );
};

export default SuggestionItems;
